import type { CamelCase, PascalCase } from "type-fest";
import { camelcasify, pascalcasify } from "../utils/string";

/*                                                          */
/*                                                          */
type AnyBemElementBuilder = BemElementBuilder<any, any>;

/*                                                                   */
type ComposeKey<Block extends string, Element extends string> = Block extends ""
  ? Element
  : `${CamelCase<Block>}${PascalCase<Element>}`;

/*                                                                   */
function composeKey<Block extends string, Element extends string>(
  block: Block,
  element: Element
): ComposeKey<Block, Element> {
  return camelcasify(block).concat(pascalcasify(element)) as never;
}

/*                                                                           */
type ComposeElement<Block extends string, Element extends string> = Block extends ""
  ? Element
  : `${Block}__${Element}`;

/*                                                                           */
function composeElement<Block extends string, Element extends string>(
  block: Block,
  element: Element
): ComposeElement<Block, Element> {
  return block.concat("__", element) as never;
}

/*                                                                            */
type ComposeModifier<
  Block extends string,
  Element extends string,
  Modifier extends string
> = Block extends "" ? `${Element}--${Modifier}` : `${Block}__${Element}--${Modifier}`;

/*                                                                            */
function composeModifier<Block extends string, Element extends string, Modifier extends string>(
  block: Block,
  element: Element,
  modifier: Modifier
): ComposeModifier<Block, Element, Modifier> {
  return (block ? block.concat("__") : "").concat(element, "--", modifier) as never;
}

/*                                                                     */
type BemUnwrapElement<
  Block extends string,
  Element extends AnyBemElementBuilder
> = Element extends BemElementBuilder<infer Name, infer Modifiers>
  ? { [P in CamelCase<Name>]: ComposeElement<Block, Name> } & {
      [P in Modifiers[number] as ComposeKey<Name, P>]: ComposeModifier<Block, Name, P>;
    }
  : never;

/*                                         */
export type BemUnwrap<
  Block extends string,
  Modifiers extends string[],
  Elements extends AnyBemElementBuilder[]
> = { main: Block } & {
  [P in Modifiers[number] as ComposeKey<"main", P>]: ComposeModifier<"", Block, P>;
} & UnionToIntersection<{ [P in keyof Elements]: BemUnwrapElement<Block, Elements[P]> }[number]>;

/*                                                                                */
function bemUnwrap<
  Prefix extends string,
  Name extends string,
  Modifiers extends string[],
  Elements extends AnyBemElementBuilder[]
>(prefix: Prefix, name: Name, modifiers: Modifiers, elements: Elements): never {
  /**
 *
 */
  const res = [["main", name]] as [string, string][];
  modifiers.forEach((m) => res.push([composeKey("main", m), composeModifier("", name, m)]));
  elements.forEach((e) => res.push(...e(name)));

  const prefixed = prefix ? res.map((r) => [r[0], prefix.concat("_", r[1])]) : res;
  const jsed = prefixed.map((r) => [`${r[0]}Js`, `js_${r[1]}`]);

  return Object.fromEntries([...prefixed, ...jsed]) as never;
}

/*                                               */
type ToPrefix<Prefix extends string> = Prefix extends "" ? Prefix : `${Prefix}_`;

type BuildBemBlock<
  Prefix extends string,
  Name extends string,
  Modifiers extends string[],
  Elements extends AnyBemElementBuilder[]
> = {
  [P in keyof BemUnwrap<Name, Modifiers, Elements>]: `${ToPrefix<Prefix>}${BemUnwrap<
    Name,
    Modifiers,
    Elements
  >[P] &
    string}`;
};

/*                                                */
export type BemBlockBuilder<
  Prefix extends string,
  Name extends string,
  Modifiers extends string[]
> = {
  /**
 *
 *
 */
  <Elements extends AnyBemElementBuilder[]>(...elements: Elements): {
    [P in keyof BuildBemBlock<Prefix, Name, Modifiers, Elements>]: BuildBemBlock<
      Prefix,
      Name,
      Modifiers,
      Elements
    >[P];
  } & {
    [P in keyof BuildBemBlock<Prefix, Name, Modifiers, Elements> as `${P &
      string}Js`]: `js_${BuildBemBlock<Prefix, Name, Modifiers, Elements>[P] & string}`;
  };
};

/*                                                                                                   */
function createBemBlockBuilder<
  Prefix extends string,
  Name extends string,
  Modifiers extends string[]
>(prefix: Prefix, name: Name, ...modifiers: Modifiers): BemBlockBuilder<Prefix, Name, Modifiers> {
  return (...elements) => bemUnwrap(prefix, name, modifiers, elements);
}

/*                                                       */
export type BemBlockFunction<Prefix extends string> = {
  /**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
  <Name extends string, Modifiers extends string[]>(
    name: Name,
    ...modifiers: Modifiers
  ): BemBlockBuilder<Prefix, Name, Modifiers>;
};

/*                                      */
export type BemBlockDefault = {
  /**
 *
 *
 *
 */
  prefix<NewPrefix extends string>(prefix: NewPrefix): BemBlockFunction<NewPrefix>;
};

function createBemBlockFunc<Prefix extends string>(prefix: Prefix): BemBlockFunction<Prefix> {
  return (name, ...modifiers) => createBemBlockBuilder(prefix, name, ...modifiers);
}

/*                                        */
export const bemBlock = createBemBlockFunc("pl") as BemBlockDefault & BemBlockFunction<"pl">;
bemBlock.prefix = createBemBlockFunc;

export type BemElementBuilder<Name extends string, Modifiers extends string[]> = {
  <Block extends string>(block: Block): [
    CamelCase<Name> | ComposeKey<Name, Modifiers[number]>,
    ComposeElement<Block, Name> | ComposeModifier<Block, Name, Modifiers[number]>
  ][];
};

function buildBemElement<Block extends string, Name extends string, Modifiers extends string[]>(
  block: Block,
  name: Name,
  modifiers: Modifiers
): [
  CamelCase<Name> | ComposeKey<Name, Modifiers[number]>,
  ComposeElement<Block, Name> | ComposeModifier<Block, Name, Modifiers[number]>
][] {
  const res: [
    CamelCase<Name> | ComposeKey<Name, Modifiers[number]>,
    ComposeElement<Block, Name> | ComposeModifier<Block, Name, Modifiers[number]>
  ][] = [[camelcasify(name), composeElement(block, name)]];
  modifiers.forEach((m) => res.push([composeKey(name, m), composeModifier(block, name, m)]));
  return res;
}

/**
 *
 *
 *
 *
 */
export function bemElement<Name extends string, Modifiers extends string[]>(
  name: Name,
  ...modifiers: Modifiers
): BemElementBuilder<Name, Modifiers> {
  return (block) => buildBemElement(block, name, modifiers);
}
